/**
 * La ventana de la aplicacion instalada: que modo es, y curar el viewport de
 * WebKit cuando se queda encogido.
 *
 * EL ALTO YA NO SE MIDE AQUI, Y ESA ES LA CONCLUSION DE LA INVESTIGACION
 * ---------------------------------------------------------------------
 * Este archivo empezo midiendo con `visualViewport` y publicando pixeles en
 * `--app-vh`. Estaba equivocado, y la documentacion del defecto lo dice con
 * numeros: en la PWA instalada de iOS, `window.innerHeight`,
 * `visualViewport.height` y `100dvh` **caen los tres juntos** —932 -> 873 en un
 * iPhone Pro Max, o sea exactamente `safe-area-inset-top`—. Medir con ellos no
 * encuentra los pixeles que faltan: los tres cuentan la misma mentira.
 *
 * Son DOS defectos distintos de WebKit con el mismo sintoma, y hay que
 * separarlos porque la cura no es la misma:
 *
 * 1. EL ALTO EN FRIO. Con `viewport-fit=cover` y la barra de estado
 *    translucida, el documento se corre HACIA ARRIBA para ocupar la muesca,
 *    pero el alto del viewport se queda con la medida de antes. Resultado: el
 *    marco nace corto por abajo justo lo que mide la muesca.
 *      - `height: 100%` NO lo ve: el porcentaje no cuenta el area de atras de
 *        la barra de estado. **Es lo que hacia el intento anterior** —el marco
 *        heredando el `100%` del cuerpo anclado— y por eso seguia corto.
 *      - `100dvh` tampoco: la unidad dinamica no queda inicializada en el
 *        arranque en frio de una PWA; el motor no la calcula hasta que el
 *        viewport se "ejercita" con un cambio de geometria, o sea hasta que
 *        alguien gira el aparato.
 *      - `100vh` SI. En modo instalado no hay barra de navegador que cree la
 *        discrepancia clasica entre `vh` y `dvh`, asi que `100vh`, `100dvh`,
 *        `innerHeight` y `screen.height` convergen — y `vh` es la unica
 *        correcta desde el primer cuadro. Eso vive en `theme.css`, en una
 *        linea, sin JavaScript.
 *
 *    Y explica por que el intento con `100vh` se descarto por equivocado: no
 *    fallaba `vh`, fallaba anclar el cuerpo con `inset: 0`. Ese ancla lo clava
 *    al viewport CHICO y, con `overflow: hidden` encima, recorta el marco. Hoy
 *    el cuerpo toma su alto de `--app-vh` como todos.
 *
 * 2. EL ENCOGIMIENTO POR TECLADO, que es lo que este archivo si atiende. En la
 *    PWA instalada, cuando se abre el teclado el viewport se encoge **y no
 *    vuelve a crecer** al cerrarse: se queda asi el resto de la sesion, hasta
 *    que alguien cierra la aplicacion del conmutador. Y encoge justo la misma
 *    cantidad, de ahi que el sintoma sea identico al de arriba. En una caja
 *    registradora esto no es un caso raro: el buscador de productos abre el
 *    teclado en la primera venta del dia.
 *
 *    Lo unico que lo cura es obligar a WebKit a MEDIR OTRA VEZ, y lo unico que
 *    lo obliga es un cambio de geometria de verdad: apagar y encender el
 *    `display` de un elemento que ocupe el alto completo. No lo arreglan ni los
 *    escuchadores de eventos, ni mover el foco, ni `interactive-widget`, que en
 *    modo instalado se ignora.
 */

/** Cuanto puede faltar antes de considerarlo un encogimiento. */
const TOLERANCIA_PX = 4;

/**
 * Techo del encogimiento que este archivo cura.
 *
 * El defecto de WebKit se lleva el alto del area segura de arriba: 59 px en los
 * iPhone con isla, 44 en los de muesca, 20 en los de boton. Un teclado abierto
 * se lleva TRESCIENTOS y pico, y ese no es un defecto que curar: son pixeles
 * que de verdad no estan. Sin este techo, en Android —donde el teclado si
 * encoge la ventana— la cura se dispararia a media captura y volveria a pintar
 * la pantalla debajo del dedo.
 */
const ENCOGIMIENTO_MAXIMO_PX = 120;

/** Entre una cura y la siguiente. Sanar dos veces seguidas no cura mas. */
const ESPERA_ENTRE_CURAS_MS = 1200;

/**
 * Momentos, en milisegundos, en que se vuelve a revisar tras irse el teclado.
 * iOS tarda varios cuadros en asentar el viewport; revisar en el mismo instante
 * mide el estado de en medio.
 */
const REVISIONES_MS = [180, 420, 900];

/**
 * Las cuatro señales de "esto se abrio como aplicacion, no como pagina".
 *
 * Cada plataforma usa la suya y con cualquiera basta. `navigator.standalone` es
 * la unica que sirve en iOS: una app agregada a la pantalla de inicio se lanza
 * por el mecanismo heredado de Apple y no pasa por el `display` del manifiesto,
 * asi que `display-mode: standalone` puede no coincidir ahi. Ese fue el defecto
 * que dejo un arreglo entero sin ejecutarse en el unico aparato donde hacia
 * falta.
 */
function instalada(): boolean {
  try {
    const m = (q: string): boolean => window.matchMedia(q).matches;
    return (
      m('(display-mode: standalone)') ||
      m('(display-mode: fullscreen)') ||
      m('(display-mode: window-controls-overlay)') ||
      (window.navigator as { standalone?: boolean }).standalone === true
    );
  } catch {
    // Sin respuesta, el caso seguro es "navegador": no ancla el cuerpo y deja
    // que la pagina se comporte como pagina.
    return false;
  }
}

/** Si hay un campo con el foco, el teclado esta arriba o esta por subir. */
function tecleando(): boolean {
  const activo = document.activeElement;
  if (!activo) return false;
  const etiqueta = activo.tagName;
  return (
    etiqueta === 'INPUT' ||
    etiqueta === 'TEXTAREA' ||
    etiqueta === 'SELECT' ||
    (activo as HTMLElement).isContentEditable === true
  );
}

/**
 * Obliga a WebKit a volver a medir el viewport.
 *
 * El unico gesto que lo consigue es un cambio de geometria de verdad sobre un
 * elemento que ocupe el alto completo: apagar su `display`, forzar el reflujo
 * leyendo una medida, y volver a encenderlo. `#root` sirve porque su hijo es el
 * marco de la aplicacion, que mide la ventana entera.
 *
 * Se guarda y se repone el desplazamiento de todo lo que estuviera desplazado.
 * Sin eso, curar el viewport a media venta manda el ticket al principio y la
 * persona pierde de vista el renglon que estaba mirando — la cura se sentiria
 * peor que el defecto.
 */
function sanar(): void {
  const raiz = document.getElementById('root');
  if (!raiz) return;

  const desplazados: Array<[Element, number]> = [];
  for (const el of raiz.querySelectorAll('*')) {
    if (el.scrollTop > 0) desplazados.push([el, el.scrollTop]);
  }

  raiz.style.display = 'none';
  // Leer una medida entre las dos escrituras es lo que fuerza el reflujo; sin
  // esto el navegador junta las dos y no pasa nada.
  void raiz.offsetHeight;
  raiz.style.display = '';

  for (const [el, top] of desplazados) el.scrollTop = top;
}

/**
 * Enciende la vigilancia. Devuelve la funcion que la apaga.
 *
 * Se llama desde `main.tsx` antes de montar React, para que la marca
 * `data-standalone` —de la que cuelga el ancla del cuerpo— este puesta cuanto
 * antes. El alto no depende de esto: lo resuelve el CSS.
 */
export function installViewport(): () => void {
  const raiz = document.documentElement;
  const temporizadores: number[] = [];
  let maximoVisto = 0;
  let ultimaCura = 0;

  const marcar = (): void => {
    // El script en linea del `<head>` ya la puso antes del primer pintado; esto
    // la mantiene al dia si el modo cambia en caliente (instalar o desinstalar
    // la PWA de escritorio) y la repone si aquel script no llego a correr.
    raiz.toggleAttribute('data-standalone', instalada());
  };

  const recordar = (): void => {
    // El techo de la sesion se toma con el teclado abajo: con el teclado arriba
    // la ventana esta legitimamente chica y guardarlo enseñaria un techo falso.
    if (!tecleando()) maximoVisto = Math.max(maximoVisto, window.innerHeight);
  };

  const revisar = (): void => {
    if (!instalada() || tecleando()) return;
    const falta = maximoVisto - window.innerHeight;
    if (falta <= TOLERANCIA_PX || falta > ENCOGIMIENTO_MAXIMO_PX) return;
    const ahora = performance.now();
    if (ahora - ultimaCura < ESPERA_ENTRE_CURAS_MS) return;
    ultimaCura = ahora;
    sanar();

    /*
     * Y SI LA CURA NO CURO, SE ACEPTA LA REALIDAD.
     *
     * Sin esto, un aparato donde el truco no funcione —una version de iOS que
     * lo cambie, un modo que no contemplamos— quedaria intentandolo en cada
     * `focusout` para siempre: un reflujo del arbol entero cada vez que alguien
     * sale de un campo, en la pantalla donde se cobra. Si tras la cura la
     * ventana sigue chica, esa ES su medida y se adopta como techo. Si algun dia
     * crece de verdad, `recordar` vuelve a subir el techo solo.
     */
    temporizadores.push(
      window.setTimeout(() => {
        if (maximoVisto - window.innerHeight > TOLERANCIA_PX) maximoVisto = window.innerHeight;
      }, 250),
    );
  };

  /** Revisa ahora y otra vez mientras el sistema se acomoda. */
  const revisarPronto = (): void => {
    revisar();
    for (const ms of REVISIONES_MS) temporizadores.push(window.setTimeout(revisar, ms));
  };

  marcar();
  recordar();

  /*
   * Redimensionar solo APUNTA el techo; no cura.
   *
   * Curar desde aqui tendria un falso positivo garantizado: en la PWA de
   * escritorio, achicar la ventana con el raton es un encogimiento legitimo de
   * menos de 120 px, o sea indistinguible del defecto, y dispararia un reflujo
   * del arbol entero mientras la persona arrastra la esquina. El defecto que
   * este archivo cura aparece SIEMPRE tras el teclado, asi que se revisa donde
   * el teclado deja rastro: al perder el foco y al volver a la aplicacion.
   */
  const vv = window.visualViewport;

  window.addEventListener('resize', recordar);
  vv?.addEventListener('resize', recordar);
  // El teclado que se va: es el momento exacto en que aparece el defecto, y iOS
  // tarda unos cuadros en dejar la medida quieta.
  window.addEventListener('focusout', revisarPronto);
  // Volver a la aplicacion desde el conmutador, y el regreso desde la cache de
  // atras/adelante: los dos re-crean el viewport.
  document.addEventListener('visibilitychange', revisarPronto);
  window.addEventListener('pageshow', revisarPronto);
  window.addEventListener('orientationchange', revisarPronto);

  const mq = window.matchMedia('(display-mode: standalone)');
  mq.addEventListener('change', marcar);

  return () => {
    for (const t of temporizadores) window.clearTimeout(t);
    window.removeEventListener('resize', recordar);
    vv?.removeEventListener('resize', recordar);
    window.removeEventListener('focusout', revisarPronto);
    document.removeEventListener('visibilitychange', revisarPronto);
    window.removeEventListener('pageshow', revisarPronto);
    window.removeEventListener('orientationchange', revisarPronto);
    mq.removeEventListener('change', marcar);
  };
}
